import { Button } from "@pocketsign/design-system";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { z } from "zod";
import { css } from "../../../styled-system/css";
import psSymbol from "../../assets/ps-symbol.png";
import { getQRCodeURL } from "../../libs/ps";

const searchSchema = z.object({
  qrId: z.string().optional(),
});

type Search = z.infer<typeof searchSchema>;

const CUSHION_SERVICE_ID = import.meta.env.VITE_CUSHION_SERVICE_ID;

function generateUrl(storeSlug: string, qrId?: string) {
  const tag = qrId && qrId.length > 0 ? `?t=${qrId}` : "";
  return getQRCodeURL(CUSHION_SERVICE_ID, `/s/${storeSlug}${tag}`);
}

function formatStoreName(storeName: string) {
  return storeName.length > 18 ? `${storeName.slice(0, 18)}...` : storeName;
}

export const Route = createFileRoute("/store/qrcode")({
  validateSearch: (search: Search) => searchSchema.parse(search),
  loaderDeps: ({ search: { qrId } }: { search: Search }) => ({
    qrId,
  }),
  loader: ({ context: { store }, deps: { qrId } }) => {
    const storeSlug = store.slug?.trim();

    if (!storeSlug) {
      throw redirect({ to: "/store/dashboard" });
    }

    return {
      qrCodeUrl: generateUrl(storeSlug, qrId),
      qrId,
      store,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { store, qrCodeUrl, qrId } = Route.useLoaderData();
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: "QRコード",
    pageStyle: `
      @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&display=swap');
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        html, body {
          height: 100vh;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden;
          -webkit-print-color-adjust: exact;
          font-family: "Noto Sans JP", sans-serif;
          font-optical-sizing: auto;
        }
      }
    `,
  } as const);

  return (
    <div
      className={css({
        minH: "calc(100vh - 52px)",
        p: { base: "20px", md: "40px" },
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      })}
    >
      <div
        ref={contentRef}
        className={css({
          bg: "white",
          border: "1px solid",
          borderColor: "border.secondary",
          borderRadius: "16px",
          flex: 1,
          minH: "480px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "@media print": {
            w: "100%",
            h: "100vh",
            minH: "100vh",
            p: "0",
            border: "none",
            borderRadius: "0",
          },
        })}
      >
        <style type="text/css" media="print">
          {`
            @page {
              size: A4;
              margin: 0;
            }
          `}
        </style>

        <div
          style={{ width: "6cm", height: "6cm" }}
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row",
            border: "1px dashed",
            borderColor: "border.secondary",
            p: "6mm",
            boxSizing: "border-box",
            position: "relative",
          })}
        >
          <div
            className={css({
              transform: "rotate(-90deg)",
              fontSize: "2.6mm",
              whiteSpace: "nowrap",
              h: "4mm",
              w: "full",
              position: "absolute",
              top: "2.8cm",
              left: "3.2cm",
              textAlign: "center",
              bottom: "50%",
              maxW: "52mm",
              overflow: "hidden",
              textOverflow: "ellipsis",
            })}
          >
            {qrId}
          </div>
          <div
            className={css({
              fontSize: "2.6mm",
              h: "4mm",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              position: "absolute",
              bottom: "0",
              left: 0,
              right: 0,
              textAlign: "center",
            })}
          >
            {formatStoreName(store.name)}
          </div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            })}
          >
            <QRCodeSVG
              value={qrCodeUrl}
              size={226}
              level="H"
              className={css({
                w: "full",
                h: "full",
              })}
            />

            <div
              className={css({
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: "1",
                p: "1mm",
                bg: "white",
                borderRadius: "4px",
              })}
            >
              <img
                src={psSymbol}
                alt="PS"
                style={{
                  width: "8mm",
                  height: "8mm",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={css({
          display: "flex",
          justifyContent: "center",
        })}
      >
        <Button
          type="button"
          onClick={() => handlePrint()}
          className={css({
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
          })}
        >
          印刷する
        </Button>
      </div>
    </div>
  );
}
